import { PrintExampleContextType } from '@/context';
import {
  ReactiveController,
  ReactiveControllerHost,
  ReactiveElement,
} from 'lit';

/**
 * The `PrintExampleEventController` class implements the `ReactiveController` interface
 * to manage the print example context of a host element. It listens for custom events
 * to update the print example context and triggers updates in the host element.
 *
 * The class constructor initializes the host and context, and adds the controller to the host.
 * The `_updatePrintExample` method updates the context with new print example data and
 * requests an update in the host element.
 *
 * The `hostConnected` method adds an event listener for the `update-print-example` event
 * when the host is connected. The `hostDisconnected` method removes the event listener
 * when the host is disconnected.
 */
export class PrintExampleEventController implements ReactiveController {
  private host: ReactiveControllerHost;

  public context?: PrintExampleContextType;

  constructor(host: ReactiveControllerHost, context?: PrintExampleContextType) {
    (this.host = host).addController(this);
    this.context = context;
  }

  private _updatePrintExample({ detail: { printExample } }: CustomEvent) {
    this.context = printExample;
    /**
     * Needed to trigger the willUpdate mechanism in web component logic
     */
    this.host.requestUpdate();
  }

  hostConnected() {
    const element = this.host as ReactiveElement;

    element.addEventListener('update-print-example', event => {
      this._updatePrintExample(event);
    });
  }

  hostDisconnected() {
    const element = this.host as ReactiveElement;

    element.removeEventListener('update-print-example', event => {
      this._updatePrintExample(event);
    });
  }
}
